import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { formatEuro } from "@/lib/formatMoney";
import {
  importEingangsrechnungArtikelToStamm,
  type EingangsrechnungArtikelLine,
} from "@/lib/invoiceMail";

function parseVkInput(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function ImportToStammDialog({
  open,
  lines,
  onClose,
  onImported,
}: {
  open: boolean;
  lines: EingangsrechnungArtikelLine[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [vkById, setVkById] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const initial: Record<number, string> = {};
    for (const line of lines) {
      const ep = line.einzelpreis_netto;
      initial[line.id] = ep != null && Number.isFinite(ep) ? String(ep).replace(".", ",") : "";
    }
    setVkById(initial);
  }, [open, lines]);

  async function handleSubmit() {
    const items: Array<{ id: number; verkaufspreis: number }> = [];
    for (const line of lines) {
      const vk = parseVkInput(vkById[line.id] || "");
      if (vk === null) {
        setError(`Bitte gültigen Verkaufspreis (VK) für „${line.bezeichnung}“ eingeben.`);
        return;
      }
      items.push({ id: line.id, verkaufspreis: vk });
    }

    setLoading(true);
    setError(null);
    try {
      const res = await importEingangsrechnungArtikelToStamm(items);
      const failed = (res.results || []).filter((r) => !r.ok);
      if (failed.length > 0 && (res.created || 0) + (res.linked || 0) === 0) {
        setError(failed.map((f) => f.reason || "Fehler").join(" · "));
        return;
      }
      const parts: string[] = [];
      if (res.created) parts.push(`${res.created} angelegt`);
      if (res.linked) parts.push(`${res.linked} verknüpft`);
      if (res.skipped) parts.push(`${res.skipped} übersprungen`);
      toast.success(parts.length ? parts.join(", ") : "Übernahme abgeschlossen.");
      onImported();
      onClose();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Import fehlgeschlagen.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="In Artikelliste übernehmen" size="lg">
      <div className="space-y-4 text-sm">
        <p className="text-label-secondary">
          Für jede Position wird ein Stammartikel angelegt (oder bei vorhandener EAN verknüpft).
          Bitte den Verkaufspreis (VK) netto angeben.
        </p>

        <div className="max-h-80 overflow-auto rounded border border-separator">
          <table className="w-full min-w-[520px] text-left text-xs">
            <thead className="sticky top-0 bg-card text-label-secondary">
              <tr>
                <th className="px-2 py-1.5 font-medium">Bezeichnung</th>
                <th className="px-2 py-1.5 font-medium text-right">EP netto</th>
                <th className="px-2 py-1.5 font-medium text-right">VK netto *</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id} className="border-t border-separator/60">
                  <td className="px-2 py-1.5 align-top">
                    <div className="font-medium text-label-primary">{line.bezeichnung}</div>
                    {line.seller_article_nr ? (
                      <div className="text-label-secondary">Art.-Nr. {line.seller_article_nr}</div>
                    ) : null}
                  </td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums whitespace-nowrap">
                    {formatEuro(line.einzelpreis_netto, 2)}
                  </td>
                  <td className="px-2 py-1.5 text-right align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-28 rounded border border-separator bg-card px-2 py-1 text-right text-sm"
                      value={vkById[line.id] ?? ""}
                      onChange={(e) =>
                        setVkById((prev) => ({ ...prev, [line.id]: e.target.value }))
                      }
                      placeholder="0,00"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={loading || lines.length === 0}>
            {loading ? <Spinner className="h-3.5 w-3.5" /> : "Übernehmen"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
